import React, { useEffect, useState } from "react";
import { Button, Space, Table, Tag, Tooltip, Modal, Popconfirm } from "antd";
import { ColumnType } from "antd/lib/table";
import { motion } from "framer-motion";
import {
    CrownOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
    UsergroupAddOutlined,
    SettingOutlined
} from "@ant-design/icons";
import { IVipTier } from "../../common/vip.interface";
import { useDispatchRoot, useSelectorRoot } from "../../redux/store";
import {
    getVipTiersRequest,
    addVipTierRequest,
    editVipTierRequest,
    deleteVipTierRequest,
    getVipUsersRequest
} from "../../redux/controller";
import CModalVipTier from "../../components/modal/CModalVipTier";
import Utils from "../../utils/base-utils";
import "./vip.styles.scss";

const VipManagement = () => {
    const dispatch = useDispatchRoot();
    const { vipTiers, totalVipTiersRecords, loading, vipUsers } = useSelectorRoot((state) => state.management);
    
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingTier, setEditingTier] = useState<IVipTier | null>(null);
    const [isVipUsersModalVisible, setIsVipUsersModalVisible] = useState(false);

    useEffect(() => {
        dispatch(getVipTiersRequest());
    }, [dispatch]);

    const handleAddVipTier = () => {
        setEditingTier(null);
        setIsModalVisible(true);
    };

    const handleEditVipTier = (tier: IVipTier) => {
        setEditingTier(tier);
        setIsModalVisible(true);
    };

    const handleDeleteVipTier = (id: number) => {
        dispatch(deleteVipTierRequest({ id }));
    };

    const handleModalSubmit = (data: any) => {
        if (editingTier) {
            // Update existing tier
            dispatch(editVipTierRequest({ id: editingTier.id, ...data }));
        } else {
            // Create new tier
            dispatch(addVipTierRequest(data));
        }
        setIsModalVisible(false);
        setEditingTier(null);
    };

    const handleModalCancel = () => {
        setIsModalVisible(false);
        setEditingTier(null);
    };

    const handleViewVipUsers = () => {
        dispatch(getVipUsersRequest({}));
        setIsVipUsersModalVisible(true);
    };

    const columns: ColumnType<IVipTier>[] = [
        {
            title: "Cấp độ",
            dataIndex: "level",
            key: "level",
            width: 100,
            sorter: (a, b) => a.level - b.level,
            render: (level) => (
                <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "16px" }}>
                    {level}
                </div>
            ),
        },
        {
            title: "Tên VIP",
            dataIndex: "name",
            key: "name",
            render: (name, record) => (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <CrownOutlined style={{ color: record.color || "#faad14" }} />
                    <span style={{ fontWeight: "600" }}>{name}</span>
                </div>
            ),
        },
        {
            title: "Mô tả",
            dataIndex: "description",
            key: "description",
            ellipsis: true,
            render: (description) => (
                <Tooltip title={description}>
                    <span>{description || "Không có mô tả"}</span>
                </Tooltip>
            ),
        },
        {
            title: "Điểm yêu cầu",
            dataIndex: "requiredPoints",
            key: "requiredPoints",
            width: 120,
            sorter: (a, b) => a.requiredPoints - b.requiredPoints,
            render: (points) => (
                <div style={{ textAlign: "center", fontWeight: "500" }}>
                    {Utils.formatNumber(points)}
                </div>
            ),
        },
        {
            title: "Giảm giá",
            dataIndex: "discountPercent",
            key: "discountPercent",
            width: 100,
            render: (percent, record) => (
                <div style={{ textAlign: "center" }}>
                    <div style={{ fontWeight: "bold", color: "#52c41a" }}>{percent}%</div>
                    {record.maxDiscount && (
                        <div style={{ fontSize: "12px", color: "#666" }}>
                            Tối đa: {Utils.formatMoney(record.maxDiscount)}
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: "Màu",
            dataIndex: "color",
            key: "color",
            width: 80,
            render: (color) => (
                <div style={{ textAlign: "center" }}>
                    {color ? (
                        <div
                            style={{
                                width: 32,
                                height: 32,
                                backgroundColor: color,
                                border: "1px solid #d9d9d9",
                                borderRadius: "4px",
                                margin: "0 auto",
                            }}
                        />
                    ) : (
                        <span style={{ color: "#999" }}>Mặc định</span>
                    )}
                </div>
            ),
        },
        {
            title: "Trạng thái",
            dataIndex: "isActive",
            key: "isActive",
            width: 100,
            render: (isActive) => (
                <div style={{ textAlign: "center" }}>
                    <Tag color={isActive ? "success" : "default"}>
                        {isActive ? "Hoạt động" : "Tạm ngưng"}
                    </Tag>
                </div>
            ),
        },
        {
            title: "Thao tác",
            key: "action",
            width: 150,
            render: (_, record) => (
                <Space size="small" style={{ display: "flex", justifyContent: "center" }}>
                    <Tooltip title="Chỉnh sửa">
                        <Button
                            type="link"
                            icon={<EditOutlined />}
                            onClick={() => handleEditVipTier(record)}
                            size="small"
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Bạn có chắc chắn muốn xóa cấp độ VIP này?"
                        onConfirm={() => handleDeleteVipTier(record.id)}
                        okText="Xóa"
                        cancelText="Hủy"
                        okType="danger"
                    >
                        <Tooltip title="Xóa">
                            <Button
                                type="link"
                                icon={<DeleteOutlined />}
                                danger
                                size="small"
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const vipUsersColumns: ColumnType<any>[] = [
        {
            title: "Tên người dùng",
            dataIndex: ["user", "username"],
            key: "username",
        },
        {
            title: "Họ tên",
            render: (_, record) => `${record.user.firstname} ${record.user.lastname}`,
        },
        {
            title: "VIP",
            dataIndex: ["vipTier", "name"],
            key: "vipName",
            render: (name, record) => (
                <Tag icon={<CrownOutlined />} color="gold">
                    {name} (Lv.{record.vipTier.level})
                </Tag>
            ),
        },
        {
            title: "Điểm hiện tại",
            dataIndex: ["user", "points"],
            key: "points",
            render: (points) => Utils.formatNumber(points),
        },
        {
            title: "Đạt VIP",
            dataIndex: "achievedAt",
            key: "achievedAt",
            render: (date) => new Date(date).toLocaleDateString("vi-VN"),
        },
    ];

    return (
        <motion.div
            className="vip-management"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        >
            <div className="vip-header">
                <div className="vip-title">
                    <CrownOutlined style={{ fontSize: "24px", color: "#faad14" }} />
                    <h2>Quản lý cấp độ VIP</h2>
                </div>
                <Space>
                    <Button
                        type="default"
                        icon={<EyeOutlined />}
                        onClick={handleViewVipUsers}
                    >
                        Xem người dùng VIP
                    </Button>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAddVipTier}
                    >
                        Thêm cấp độ VIP
                    </Button>
                </Space>
            </div>

            <div className="vip-table-container">
                <Table
                    columns={columns}
                    dataSource={vipTiers}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        total: totalVipTiersRecords,
                        showSizeChanger: false,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} của ${total} cấp độ VIP`,
                    }}
                    size="middle"
                    className="vip-table"
                />
            </div>

            <CModalVipTier
                visible={isModalVisible}
                onCancel={handleModalCancel}
                onSubmit={handleModalSubmit}
                editData={editingTier}
                existingTiers={vipTiers}
            />

            <Modal
                title={
                    <Space>
                        <UsergroupAddOutlined />
                        <span>Danh sách người dùng VIP</span>
                    </Space>
                }
                open={isVipUsersModalVisible}
                onCancel={() => setIsVipUsersModalVisible(false)}
                footer={null}
                width={800}
            >
                <Table
                    columns={vipUsersColumns}
                    dataSource={vipUsers?.users || []}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        total: vipUsers?.totalCount || 0,
                        current: vipUsers?.page || 1,
                        pageSize: vipUsers?.limit || 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} của ${total} người dùng VIP`,
                    }}
                    size="small"
                />
            </Modal>
        </motion.div>
    );
};

export default VipManagement;
